import moment from 'moment'
import { v4 as uuidv4 } from 'uuid'
import {
    CartItemCardDetailDto,
    useCreateCartItemsMutation,
    useDeleteCartItemsByIdMutation,
    useDeleteCartItemByIdMutation,
    useDeleteCartMutation,
    useUploadCartItemCardImageMutation,
    CreateCartItemCommand2,
    CreateCartItemBundleCommand,
    CreateCartItemImageCommand,
} from 'store/api-slice/hooks'
import { Card, CardBox } from 'store/api-slice/types'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { useApp } from 'store/steps'
import { useCart } from 'store/cart-slice/hooks'
import { setIsErrorModalOpened } from 'store/general-slice'
import { ErrorField, handleError } from 'helpers/errors'
import { PromiseResponse } from 'helpers/types'
import { CART_TYPE_CARD, CART_TYPE_CARD_RENEW } from 'constants/app'
import { URLwithStore } from 'routes/unlimited-card/helpers'
import { removeAllCards } from '.'

export const useUnlimited = (): {
    addCardsToCart: (isRenewed?: boolean) => Promise<PromiseResponse>
    removeCardsFromCart: (
        isFullReset?: boolean,
        shouldSkipRefetch?: boolean
    ) => Promise<PromiseResponse>
    clearItemsFromCart: () => Promise<PromiseResponse>
    isProcessing: boolean
    removeShipping: () => Promise<void>
} => {
    const dispatch = useAppDispatch()

    const { cardBoxes } = useAppSelector((state) => state.unlimited)
    const {
        items: { cards, shipping, voucher },
    } = useAppSelector((state) => state.cart)

    const { language, appMode } = useApp()
    const { refetchCart } = useCart()

    const [postCartItems, postStatus] = useCreateCartItemsMutation()
    const [postCardImage, uploadStatus] = useUploadCartItemCardImageMutation()
    const [deleteCart, deleteStatus] = useDeleteCartMutation()
    const [deleteItems, deleteItemsStatus] = useDeleteCartItemsByIdMutation()
    const [deleteItem, deleteItemStatus] = useDeleteCartItemByIdMutation()

    const cartCreatorType =
        appMode === 'unlimited-renew' ? CART_TYPE_CARD_RENEW : CART_TYPE_CARD

    const isProcessing =
        postStatus.isLoading ||
        uploadStatus.isLoading ||
        deleteStatus.isLoading ||
        deleteItemsStatus.isLoading ||
        deleteItemStatus.isLoading

    const uploadImages = async () => {
        const imagesToUpload: { cartItemId: string; image: string }[] = []

        cardBoxes.forEach(({ cartItemId, image }: CardBox) => {
            if (!cartItemId || !image) return { isSuccess: false }
            imagesToUpload.push({ cartItemId, image })
        })

        //eslint-disable-next-line compat/compat
        await Promise.all(
            imagesToUpload.map(async ({ cartItemId, image }) => {
                const retrievedBlob = URLwithStore.getFromObjectURL(image)
                const imgFile = new File([retrievedBlob], 'avatar.jpeg', {
                    type: 'image/jpeg',
                })
                const imgData = new FormData()

                imgData.append('cartItemId', cartItemId)
                imgData.append('type', CART_TYPE_CARD)
                imgData.append('image', imgFile)

                const response = await postCardImage({
                    // TODO: check different way to send files in rtk query
                    // now formdata is not compatible with declared types
                    // and ts do not catch all possible errors after changes
                    createCartItemImageCommand:
                        imgData as unknown as CreateCartItemImageCommand,
                    _locale: language,
                })
                return handleError(response)
            })
        )
    }

    const addCardsToCart = async (isRenewed = false) => {
        const cardsToSend: CreateCartItemCommand2[] = []
        const bundlesToSend: CreateCartItemBundleCommand[] = []
        let hasInnerError = false

        const cardsOnlyBoxes = cardBoxes.filter((item) => !item?.bundleData)
        const bundlesBoxes = cardBoxes.filter((item) => item?.bundleData)

        cardsOnlyBoxes.forEach(
            ({
                cartItemId,
                personDetail,
                cardDetail,
                productId,
                firstName,
                lastName,
                dateOfBirth,
                email,
                phone,
            }: CardBox) => {
                if (hasInnerError) return
                if (
                    !cartItemId ||
                    !personDetail ||
                    !personDetail.key ||
                    !cardDetail ||
                    !cardDetail.cardType ||
                    !productId ||
                    !firstName ||
                    !lastName ||
                    !dateOfBirth ||
                    !email ||
                    !phone
                ) {
                    hasInnerError = true
                    return
                }

                const cardDetails: CartItemCardDetailDto = {
                    person: personDetail.key,
                    cardType: cardDetail.cardType.value,
                    firstName,
                    lastName,
                    dateOfBirth,
                    email,
                    phone,
                }

                cardsToSend.push({
                    cartItemId,
                    productType: 'card',
                    productId,
                    quantity: 1,
                    detail: { ...cardDetails },
                })
            }
        )

        bundlesBoxes.forEach(
            ({
                cartItemId,
                productId,
                personDetail,
                cardDetail,
                firstName,
                lastName,
                dateOfBirth,
                email,
                phone,
                bundleData,
            }: CardBox) => {
                if (hasInnerError) return
                if (
                    !cartItemId ||
                    !productId ||
                    !bundleData ||
                    !bundleData.length ||
                    !personDetail ||
                    !personDetail.key ||
                    !cardDetail ||
                    !cardDetail.cardType ||
                    !firstName ||
                    !lastName ||
                    !dateOfBirth ||
                    !email ||
                    !phone
                ) {
                    hasInnerError = true
                    return
                }

                const cartItemBundleId = uuidv4()
                const itemsToSend: CreateCartItemCommand2[] = []

                bundleData.forEach((item) => {
                    const { productType, productId } = item

                    if (hasInnerError) return

                    if (!productType || !productId) {
                        hasInnerError = true
                        return
                    }

                    const commonData = {
                        productId,
                        quantity: 1,
                        cartItemBundleId,
                        cartCreatorType,
                    }

                    if (productType === 'parking') {
                        const dateNow = moment(Date.now())
                        const dateForAYear = moment(Date.now())
                            .add(1, 'year')
                            .subtract(1, 'days')

                        itemsToSend.push({
                            ...commonData,
                            productType: 'parking',
                            cartItemId: uuidv4(),
                            detail: {
                                from: dateNow.format('YYYY-MM-DD'),
                                to: dateForAYear.format('YYYY-MM-DD'),
                            },
                        })
                    } else if (productType === 'card') {
                        if (!personDetail.key) {
                            hasInnerError = true
                            return
                        }
                        itemsToSend.push({
                            ...commonData,
                            productType: 'card',
                            cartItemId,
                            detail: {
                                person: personDetail.key,
                                cardType: cardDetail.cardType.value,
                                firstName,
                                lastName,
                                dateOfBirth,
                                email,
                                phone,
                            },
                        })
                    }
                })

                bundlesToSend.push({
                    cartItemBundleId,
                    productId,
                    quantity: 1,
                    items: [...itemsToSend],
                })
            }
        )

        if (hasInnerError) return { isSuccess: false }

        try {
            handleError(
                await postCartItems({
                    createCartItemsCommand: {
                        items: cardsToSend,
                        itemsBundle: bundlesToSend,
                    },
                    cartCreatorType,
                    _locale: language,
                })
            )
        } catch (errors: unknown) {
            const { messages } = errors as {
                messages: ErrorField[]
            }
            if (!messages) dispatch(setIsErrorModalOpened(true))
            return { isSuccess: false, messages }
        }

        if (isRenewed) {
            refetchCart(true)
            return { isSuccess: true }
        }

        try {
            handleError(await uploadImages())
            refetchCart(true)
            return { isSuccess: true }
        } catch (error) {
            dispatch(setIsErrorModalOpened(true))
            return { isSuccess: false }
        }
    }

    const removeCardsFromCart = async (
        isFullReset = false,
        shouldSkipRefetch = false
    ) => {
        try {
            handleError(
                await deleteCart({
                    _locale: language,
                    cartCreatorType,
                })
            )
            if (isFullReset) dispatch(removeAllCards())
            if (!shouldSkipRefetch) refetchCart()
            return { isSuccess: true }
        } catch (errors: unknown) {
            const { messages } = errors as {
                messages: ErrorField[]
            }
            return { isSuccess: false, messages }
        }
    }

    const clearItemsFromCart = async () => {
        const shippingId = shipping?.cartItemId
        const voucherId = voucher
        const itemsToDelete: string[] = []
        if (isProcessing) return { isSuccess: true }

        try {
            shippingId && itemsToDelete.push(shippingId)
            voucherId && itemsToDelete.push(voucherId)
            cards.forEach((card) => {
                if (card.itemsBundle && card.itemsBundle.length) {
                    card.itemsBundle.forEach(({ cartItemId }) => {
                        cartItemId && itemsToDelete.push(cartItemId)
                    })
                } else card.cartItemId && itemsToDelete.push(card.cartItemId)
            })

            if (itemsToDelete.length) {
                handleError(
                    await deleteItems({
                        _locale: language,
                        cartCreatorType,
                        deleteCartItemsByIdCommand: {
                            cartItemUuids: itemsToDelete,
                        },
                    })
                )
                refetchCart()
            }

            return { isSuccess: true }
        } catch (errors: unknown) {
            const { messages } = errors as {
                messages: ErrorField[]
            }
            return { isSuccess: false, messages }
        }
    }

    const removeShipping = async () => {
        const shippingId = shipping?.cartItemId

        if (!shippingId) return
        try {
            handleError(
                await deleteItem({
                    _locale: language,
                    cartItemId: shippingId,
                    cartCreatorType,
                })
            )
            refetchCart()
        } catch (error) {
            dispatch(setIsErrorModalOpened(true))
        }
    }

    return {
        addCardsToCart,
        removeCardsFromCart,
        clearItemsFromCart,
        removeShipping,
        isProcessing,
    }
}

// sorting in order: card_type -> person_types -> yes/not discounted
export const useSortCardsByPerson = () => {
    const { items } = useAppSelector((state) => state.cart)
    const { cards } = items

    // sorting card types
    const unlimitedCards = cards.filter(
        (card) =>
            card.detail?.cardType.includes('unlimited') &&
            !card.detail?.cardType.includes('plus')
    )
    const unlimitedPlusCards = cards.filter((card) =>
        card.detail?.cardType.includes('plus')
    )

    type CardToDisplay = {
        name: string
        description: string
        quantity: number
        unitPrice: number
        cartItemId: string
        totalPrice: number
        totalPriceWithoutDiscount: number
        hasPromotion: boolean
    }

    // sorting person types and discounts
    const sortAndGroupCards = (array: Card[]) => {
        if (array.length === 0) return []

        const personTypes = Array.from(
            new Set(array.map((item) => item.detail.person))
        )

        let cardsToDisplay: CardToDisplay[] = []

        personTypes.forEach((item) => {
            const cardsFilteredForPerson: Card[] = array.filter(
                (card) => card.detail.person === item
            )
            // sorting discounts
            const cardsReducedByPrices = cardsFilteredForPerson.reduce(
                (accumulator: CardToDisplay[], currentValue: Card) => {
                    const {
                        name,
                        unitPrice,
                        cartItemId,
                        description,
                        totalPrice,
                        totalPriceWithoutDiscount,
                        hasPromotion,
                        quantity,
                    } = currentValue
                    const matchingAccumlatorItem = accumulator.findIndex(
                        ({
                            totalPrice: totalPriceItem,
                            hasPromotion: hasPromotionItem,
                        }) =>
                            totalPrice === totalPriceItem &&
                            hasPromotion === hasPromotionItem
                    )

                    if (matchingAccumlatorItem !== -1)
                        accumulator[matchingAccumlatorItem].quantity += 1

                    if (
                        matchingAccumlatorItem === -1 &&
                        name &&
                        cartItemId &&
                        unitPrice !== undefined &&
                        totalPrice !== undefined &&
                        quantity !== undefined &&
                        hasPromotion !== undefined &&
                        typeof totalPriceWithoutDiscount === 'number'
                    )
                        accumulator.push({
                            name,
                            description: description || '',
                            quantity,
                            unitPrice,
                            cartItemId,
                            totalPrice,
                            totalPriceWithoutDiscount,
                            hasPromotion,
                        })

                    return accumulator
                },
                []
            )

            cardsToDisplay = [...cardsToDisplay, ...cardsReducedByPrices]
        })

        return cardsToDisplay
    }

    return [
        ...sortAndGroupCards(unlimitedCards),
        ...sortAndGroupCards(unlimitedPlusCards),
    ]
}
